/**
 * The 2-D DCT-II, orthonormal.
 *
 * Written here rather than pulled from a library because it is twenty lines,
 * has no platform dependency, and needs to run unchanged in the Android plugin,
 * the browser and the catalog builder. It is the only mathematics standing
 * between a photograph and a perceptual hash, so it is asserted against the
 * textbook definition in the tests.
 *
 * Computing convention: the transform is separable, so it is applied along rows
 * and then along columns with one precomputed cosine table, which is O(n^3)
 * instead of the O(n^4) of the definition.
 *
 * Coefficient `(u, v)` - column frequency `u`, row frequency `v` - is stored at
 * `v * size + u`.
 */
export function dct2d(levels, size) {
    if (!Number.isInteger(size) || size < 1)
        throw new Error(`dct2d needs a positive integer size, got ${size}`);
    if (levels.length !== size * size) {
        throw new Error(`dct2d expected ${size * size} levels for a ${size}x${size} matrix, got ${levels.length}`);
    }
    const at = (index) => levels[index] ?? 0;
    // cos[k * size + n] = cos(pi * (2n + 1) * k / 2N)
    const cosines = new Float64Array(size * size);
    for (let k = 0; k < size; k += 1) {
        for (let n = 0; n < size; n += 1) {
            cosines[k * size + n] = Math.cos((Math.PI * (2 * n + 1) * k) / (2 * size));
        }
    }
    const orthonormalScale = (k) => (k === 0 ? Math.SQRT1_2 : 1);
    const normalise = 2 / size;
    // Rows: x -> u.
    const rows = new Float64Array(size * size);
    for (let y = 0; y < size; y += 1) {
        for (let u = 0; u < size; u += 1) {
            let sum = 0;
            for (let x = 0; x < size; x += 1)
                sum += at(y * size + x) * (cosines[u * size + x] ?? 0);
            rows[y * size + u] = sum;
        }
    }
    // Columns: y -> v.
    const out = new Float64Array(size * size);
    for (let u = 0; u < size; u += 1) {
        for (let v = 0; v < size; v += 1) {
            let sum = 0;
            for (let y = 0; y < size; y += 1)
                sum += (rows[y * size + u] ?? 0) * (cosines[v * size + y] ?? 0);
            out[v * size + u] = normalise * orthonormalScale(u) * orthonormalScale(v) * sum;
        }
    }
    return out;
}
//# sourceMappingURL=dct.js.map